import { useState, useMemo } from "react";

// Example: The logged in employee's name
const CURRENT_USER = "Amit Sharma";

// Leave types and allowed quota per type (per year, e.g.)
const LEAVE_TYPES = [
  { type: "Sick Leave", quota: 10 },
  { type: "Casual Leave", quota: 7 },
  { type: "Paid Leave", quota: 15 },
  { type: "Maternity Leave", quota: 180 }
];

export default function Leave() {
  const [leaves, setLeaves] = useState([
    {
      employee: "Amit Sharma",
      type: "Sick Leave",
      from: "2024-01-05",
      to: "2024-01-07",
      status: "Pending",
    },
    {
      employee: "Priya Patel",
      type: "Casual Leave",
      from: "2024-01-10",
      to: "2024-01-12",
      status: "Approved",
    },
    {
      employee: "Rahul Verma",
      type: "Paid Leave",
      from: "2024-01-18",
      to: "2024-01-20",
      status: "Rejected",
    },
    {
      employee: "Neha Singh",
      type: "Maternity Leave",
      from: "2024-02-01",
      to: "2024-02-28",
      status: "Approved",
    },
    {
      employee: "Amit Sharma",
      type: "Casual Leave",
      from: "2024-03-10",
      to: "2024-03-12",
      status: "Approved"
    }
  ]);

  // --- HR Stats ---
  const stats = useMemo(() => {
    return {
      total: leaves.length,
      approved: leaves.filter(l => l.status === "Approved").length,
      pending: leaves.filter(l => l.status === "Pending").length,
      rejected: leaves.filter(l => l.status === "Rejected").length
    };
  }, [leaves]);

  // --- MY LEAVE INFO ---
  // Which leaves are "mine"
  const myLeaves = useMemo(() => leaves.filter(l => l.employee === CURRENT_USER), [leaves]);
  // Compute leave count per type for "me" (approved or pending)
  const myUsage = useMemo(() => {
    const counts = {};
    for (let { type, from, to, status } of myLeaves) {
      if (status === "Rejected") continue;
      const days = dateDiffDays(from, to);
      if (!counts[type]) counts[type] = 0;
      counts[type] += days;
    }
    return counts;
  }, [myLeaves]);

  // --- APPLY LEAVE STATE
  const [form, setForm] = useState({
    employee: CURRENT_USER,
    type: "",
    from: "",
    to: "",
  });
  const [message, setMessage] = useState("");

  const handleApplyLeave = (e) => {
    e.preventDefault();
    // Form validation
    if (
      !form.employee.trim() ||
      !form.type.trim() ||
      !form.from.trim() ||
      !form.to.trim()
    ) {
      setMessage("Please fill in all fields");
      return;
    }
    if (new Date(form.to) < new Date(form.from)) {
      setMessage("To Date cannot be before From Date");
      return;
    }
    // Check quota (for current user)
    const requestedDays = dateDiffDays(form.from, form.to);

    const used = myUsage[form.type] || 0;
    const leaveTypeQuota = LEAVE_TYPES.find(l => l.type === form.type)?.quota || 0;
    if (used + requestedDays > leaveTypeQuota) {
      setMessage(
        `You are exceeding your annual quota for ${form.type} (${leaveTypeQuota} days)`
      );
      return;
    }

    setLeaves([
      ...leaves,
      {
        employee: form.employee,
        type: form.type,
        from: form.from,
        to: form.to,
        status: "Pending",
      },
    ]);
    setForm({ employee: CURRENT_USER, type: "", from: "", to: "" });
    setMessage("Leave request submitted!");
    setTimeout(() => setMessage(""), 2000);
  };

  // "Modern HR" look & feel using minimal, neat cards and progress bars
  return (
    <div className="p-8 w-full bg-[#f5f7fb] min-h-screen">
      {/* HR PAGE HEADER */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-500 mt-2">
            View your leave balance and track requests. Welcome,{" "}
            <span className="font-semibold">{CURRENT_USER}</span>!
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <img
            src={`https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(CURRENT_USER)}`}
            alt={CURRENT_USER}
            className="rounded-full h-14 w-14 border-2 border-blue-100 shadow"
          />
        </div>
      </div>

      {/* LEAVE BALANCE (My Quota) */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-7 items-stretch">
        <div className="bg-white rounded-2xl shadow-md border p-6 flex flex-col">
          <h2 className="font-bold text-lg mb-3">My Leave Balance</h2>
          <div className="space-y-4">
            {LEAVE_TYPES.map(({ type, quota }) => {
              const used = myUsage[type] || 0;
              const pct = Math.min(100, ((used / quota) * 100) || 0);
              return (
                <div key={type}>
                  <div className="flex justify-between text-sm font-medium mb-1">
                    <span>{type}</span>
                    <span>
                      {used}/{quota} days
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-xl">
                    <div
                      className={
                        "h-3 rounded-xl transition-all " +
                        (pct > 85
                          ? "bg-red-500"
                          : pct > 60
                          ? "bg-yellow-400"
                          : "bg-blue-500")
                      }
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* HR Dashboard Stats */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
          <LeaveStatBox label="All Requests" value={stats.total} icon="📋" />
          <LeaveStatBox label="Approved" value={stats.approved} icon="✅" color="green" />
          <LeaveStatBox label="Pending" value={stats.pending} icon="⏳" color="yellow" />
          <LeaveStatBox label="Rejected" value={stats.rejected} icon="❌" color="red" />
        </div>
      </div>

      {/* APPLY LEAVE FORM */}
      <div className="max-w-2xl mb-10">
        <div className="bg-white rounded-2xl p-8 shadow-md border">
          <h2 className="text-lg font-bold mb-5 text-gray-700">Apply for Leave</h2>
          {message && (
            <div
              className={`mb-4 px-3 py-2 rounded text-center font-medium ${
                message.includes("submitted")
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {message}
            </div>
          )}
          <form className="grid grid-cols-1 gap-5" onSubmit={handleApplyLeave} autoComplete="off">
            <div>
              <label className="block text-sm font-medium mb-1">Employee Name</label>
              <input
                type="text"
                className="w-full border rounded px-3 py-2 bg-gray-50"
                value={form.employee}
                readOnly
                disabled
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Leave Type</label>
              <select
                className="w-full border rounded px-3 py-2 bg-gray-50"
                value={form.type}
                onChange={e =>
                  setForm(f => ({ ...f, type: e.target.value }))
                }
                required
              >
                <option value="">Select type</option>
                {LEAVE_TYPES.map(({ type }) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-1">From Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                  value={form.from}
                  onChange={e =>
                    setForm(f => ({ ...f, from: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">To Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2 bg-gray-50"
                  value={form.to}
                  onChange={e =>
                    setForm(f => ({ ...f, to: e.target.value }))
                  }
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className="mt-2 w-full bg-blue-600 text-white px-5 py-2 rounded-lg shadow hover:bg-blue-700 transition"
            >
              Apply for Leave
            </button>
          </form>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* MY LEAVES TABLE */}
        <div className="bg-white rounded-2xl shadow-md border p-8">
          <h2 className="text-lg font-bold mb-6 text-gray-700 flex items-center gap-2">
            <span>My Leave Requests</span>
            <span className="text-xs border px-2 py-0.5 rounded bg-gray-100 font-semibold text-gray-800">
              {myLeaves.length} total
            </span>
          </h2>
          {myLeaves.length === 0 ? (
            <p className="text-gray-400 text-center py-6">You have no leave requests yet.</p>
          ) : (
            <table className="w-full text-left text-gray-700">
              <thead>
                <tr className="border-b text-[15px] text-gray-500">
                  <th className="py-2 font-semibold">Type</th>
                  <th className="py-2 font-semibold">Dates</th>
                  <th className="py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {myLeaves
                  .sort((a, b) => new Date(b.from) - new Date(a.from))
                  .map((leave, idx) => (
                    <tr
                      key={idx}
                      className={`${idx === myLeaves.length - 1 ? "" : "border-b"} hover:bg-gray-50`}
                    >
                      <td className="py-3">{leave.type}</td>
                      <td>
                        {leave.from && leave.to
                          ? `${formatDate(leave.from)} - ${formatDate(leave.to)}`
                          : ""}
                      </td>
                      <td>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            leave.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : leave.status === "Approved"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ALL LEAVE ACTIVITY TABLE */}
        <div className="bg-white rounded-2xl shadow-md border p-8">
          <h2 className="text-lg font-bold mb-6 text-gray-700">Leave Activity (All)</h2>
          <div className="overflow-auto max-h-[420px]">
            <table className="w-full text-left text-gray-700">
              <thead>
                <tr className="border-b text-[15px] text-gray-500">
                  <th className="py-2 font-semibold">Employee</th>
                  <th className="py-2 font-semibold">Type</th>
                  <th className="py-2 font-semibold">Dates</th>
                  <th className="py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {leaves
                  .sort((a, b) => new Date(b.from) - new Date(a.from))
                  .map((leave, idx) => (
                    <tr
                      key={idx}
                      className={`${idx === leaves.length - 1 ? "" : "border-b"} hover:bg-gray-50`}
                    >
                      <td className="py-3 font-medium">{leave.employee}</td>
                      <td>{leave.type}</td>
                      <td>
                        {leave.from && leave.to
                          ? `${formatDate(leave.from)} - ${formatDate(leave.to)}`
                          : ""}
                      </td>
                      <td>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            leave.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700"
                              : leave.status === "Approved"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Util Components ---
function LeaveStatBox({ label, value, icon, color }) {
  const colorMap = {
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    yellow: "bg-yellow-100 text-yellow-700",
    gray: "bg-gray-100 text-gray-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <div
      className={
        "rounded-2xl p-4 flex flex-col items-center justify-center gap-2 shadow-sm border bg-white"
      }
    >
      <span className="text-2xl">{icon}</span>
      <span className="font-bold text-xl">{value}</span>
      <span className={`text-[13px] font-semibold ${colorMap[color] || colorMap.gray}`}>
        {label}
      </span>
    </div>
  );
}

function formatDate(str) {
  if (!str) return "";
  if (isNaN(Date.parse(str))) return str;
  const date = new Date(str);
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  return `${month} ${day}`;
}

// Count days between two dates (inclusive)
function dateDiffDays(from, to) {
  try {
    const d1 = new Date(from);
    const d2 = new Date(to);
    if (isNaN(d1) || isNaN(d2)) return 0;
    return Math.abs(Math.floor((d2 - d1) / (1000 * 60 * 60 * 24))) + 1;
  } catch {
    return 0;
  }
}
